import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { LanguageSwitcher } from "@/components/language-switcher";
import { ChangePasswordTab } from "@/components/settings/change-password-tab";
import { CompanyTab } from "@/components/settings/company-tab";
import { DistrictProfileTab } from "@/components/settings/district-profile-tab";
import { EndUserProfileTab } from "@/components/settings/end-user-profile-tab";
import { ResellerManagerProfileTab } from "@/components/settings/reseller-manager-profile-tab";
import { ResellerPersonalizationTab } from "@/components/settings/reseller-personalization-tab";
import { ResellerProfileTab } from "@/components/settings/reseller-profile-tab";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/auth-context";
import { getUserRole } from "@/types/auth";

const TAB_TRIGGER_CLASS =
	"whitespace-nowrap rounded-md px-3 h-8 text-sm font-normal text-muted-foreground cursor-pointer hover:bg-primary/8 hover:text-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:font-medium md:w-full md:justify-start";

export function SettingsPage() {
	const { user } = useAuth();
	const { t } = useTranslation();
	const role = user ? getUserRole(user) : null;
	const isAdmin = role === "admin";
	const isResellerManager = role === "reseller_manager" || isAdmin;
	const isReseller = role === "reseller" || isAdmin;
	const isDistrictManager = role === "district_manager" || isAdmin;
	const isEndUser = role === "end_user";
	const [searchParams, setSearchParams] = useSearchParams();
	const tab = searchParams.get("tab") ?? "profile";

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 lg:p-6">
			<Tabs
				value={tab}
				onValueChange={(v) => setSearchParams({ tab: v })}
				orientation="vertical"
				className="w-full"
			>
				<div className="flex w-full flex-col gap-4 md:flex-row md:gap-0">
					<div className="w-full md:w-52 md:shrink-0 md:pr-4">
						<TabsList className="h-auto w-full !flex-row gap-1 overflow-x-auto rounded-lg bg-muted/50 p-1 md:!flex-col md:items-start md:gap-0.5 md:bg-muted/50 md:p-2">
							<TabsTrigger value="profile" className={TAB_TRIGGER_CLASS}>
								{t("settings.tabs.profile")}
							</TabsTrigger>
							{(isDistrictManager || isEndUser) && (
								<TabsTrigger value="password" className={TAB_TRIGGER_CLASS}>
									{t("settings.tabs.password")}
								</TabsTrigger>
							)}
							<TabsTrigger
								value="personalization"
								className={TAB_TRIGGER_CLASS}
							>
								{t("settings.tabs.personalization")}
							</TabsTrigger>
						</TabsList>
					</div>

					<div className="flex-1 min-w-0 md:pl-6">
						<TabsContent value="profile" className="mt-0">
							{isAdmin ? (
								<div className="flex flex-col gap-6">
									<DistrictProfileTab />
									<ResellerManagerProfileTab />
									<ResellerProfileTab />
								</div>
							) : isDistrictManager ? (
								<DistrictProfileTab />
							) : isEndUser ? (
								<EndUserProfileTab />
							) : isResellerManager ? (
								<ResellerManagerProfileTab />
							) : (
								<ResellerProfileTab />
							)}
						</TabsContent>

						{(isDistrictManager || isEndUser) && (
							<TabsContent value="password" className="mt-0">
								<ChangePasswordTab />
							</TabsContent>
						)}

						<TabsContent value="personalization" className="mt-0">
							<div className="flex flex-col gap-6">
								{isResellerManager && <CompanyTab />}
								{isReseller && <ResellerPersonalizationTab />}
								<Card>
									<CardContent className="flex flex-col gap-3 pt-6">
										<Label>{t("settings.preferences.language")}</Label>
										<div className="max-w-sm">
											<LanguageSwitcher variant="dark" />
										</div>
									</CardContent>
								</Card>
							</div>
						</TabsContent>
					</div>
				</div>
			</Tabs>
		</div>
	);
}
